/**
 * "Ver veículo" e "Ver associado" a partir de um ticket (spec 032, §7):
 *
 * - o recurso do SGR já ligado ao ticket: abre a página dele;
 * - o ticket importado traz o `ID_SGR:` (o `cod_veiculo`, o mesmo id da API):
 *   acha o recurso do ERP SEM ler o SGR; se ainda não existe, o cria pelo
 *   mesmo /pick (2 leituras do SGR) — o "abrir" do D1. O ticket nunca é
 *   escrito, nem o espelhado;
 * - sem id: busca pela placa e escolhe só se o resultado for exatamente 1.
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { plateKey, type EquipmentDetail } from '@erp/shared';
import { api } from '@/lib/api-client';
import { sgrPick, sgrResourceOf, sgrSearch } from './sgr-queries';
import { unavailableText, type VehicleLinkTarget } from './sgr-view';

type Destination = 'vehicle' | 'end-customer';

export function useOpenSgrVehicle(sourceName: string) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** o recurso do ERP do veículo (criado pelo /pick quando falta) e o associado dele */
  async function resolve(target: VehicleLinkTarget): Promise<{ equipmentId: number; endCustomerId: number | null } | string> {
    if (target.kind === 'equipment') return { equipmentId: target.equipmentId, endCustomerId: null };
    let sgrId: string;
    if (target.kind === 'source_id') {
      sgrId = target.sgrId;
    } else {
      const found = await sgrSearch({ q: target.plate }, AbortSignal.timeout(30_000));
      if (found.status !== 'ok' || found.kind !== 'vehicles') return unavailableText(sourceName, found.status === 'unavailable' ? found.reason : undefined);
      const exact = found.items.filter((i) => plateKey(i.plate) === target.plate);
      if (exact.length !== 1) {
        return exact.length === 0 ? `Nada encontrado no ${sourceName} com esse identificador.` : `Mais de um resultado no ${sourceName} — abra pela busca.`;
      }
      sgrId = exact[0]!.sgrId;
    }
    const existing = await sgrResourceOf(sgrId);
    if (existing !== null) return { equipmentId: existing, endCustomerId: null };
    const pick = await sgrPick(sgrId);
    if (pick.status === 'not_found') return `Não encontrado no ${sourceName}.`;
    if (pick.status !== 'ok') return unavailableText(sourceName, pick.reason);
    return { equipmentId: pick.equipment.id, endCustomerId: pick.endCustomer.id };
  }

  async function open(target: VehicleLinkTarget, destination: Destination): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const r = await resolve(target);
      if (typeof r === 'string') {
        setError(r);
        return;
      }
      if (destination === 'vehicle') {
        await navigate({ to: '/resources/$resourceId', params: { resourceId: String(r.equipmentId) } });
        return;
      }
      let endCustomerId = r.endCustomerId;
      if (endCustomerId === null) {
        const detail = await api<EquipmentDetail>(`/resources/${r.equipmentId}`);
        endCustomerId = detail.error ? null : (detail.data.endCustomer?.id ?? null);
      }
      if (endCustomerId === null) {
        setError(`Sem cadastro de origem para abrir.`);
        return;
      }
      await navigate({ to: '/end-customers/$endCustomerId', params: { endCustomerId: String(endCustomerId) } });
    } finally {
      setBusy(false);
    }
  }

  return { open, busy, error };
}
